import React, { useState } from 'react';
import { useTranslate } from 'react-admin';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import {
  Button,
  CardActions,
  CircularProgress,
  withStyles,
} from '@material-ui/core';
import {
  PasswordField,
  renderFormFieldInput as renderInput,
} from '../components';
import { formStyles as styles } from '../styles';
import { validateEmail, validateCodePassword } from '../../util/validation';

export const RequestResetPassword = withStyles(styles)(props => {
  const { requestReset } = props;
  const translate = useTranslate();
  const [isLoading, setLoading] = useState(false);

  return (
	<Form
		validate={values => {
        return validateEmail(values, { translate });
		}}
		onSubmit={values => {
        requestReset(values, setLoading);
		}}
		{...props}
	>
		{({ classes, handleSubmit, submitting }) => (
			<form onSubmit={handleSubmit}>
				<div className={classes.form}>
					<div className={classes.input}>
						<Field
							id="email"
							name="email"
							label={translate('authentication.forgotPassword.email', {
                  _: 'Email',
                })}
							render={renderInput}
							disabled={isLoading || submitting}
              />
					</div>
					<p className={classes.input}>
						{translate('authentication.forgotPassword.cardContent', {
                _:
                  'Please enter your email address above. \r\n You will receive an email with a code that you can redeem to reset your password.',
              })}
					</p>
				</div>
				<div className={classes.cardActions}>
					<CardActions>
						<Button
							type="submit"
							color="primary"
							disabled={isLoading}
							className={classes.button}
						>
							{isLoading ? (
								<CircularProgress
									size={25}
									thickness={3}
									className={classes.loadingIndicator}
                  />
                ) : (
                  translate('authentication.forgotPassword.submit', {
                    _: 'Reset Password',
                  })
                )}
						</Button>
					</CardActions>
				</div>
			</form>
      )}
	</Form>
  );
});

export const RequestChangePassword = withStyles(styles)(props => {
  const { requestChangePassword, overrideFormValidation } = props;
  const translate = useTranslate();
  const [isLoading, setLoading] = useState(false);
  const validate = values =>
    overrideFormValidation
      ? overrideFormValidation(values, { translate })
      : validateCodePassword(values, { translate });

  return (
	<Form
		validate={values => {
        return validate(values);
		}}
		onSubmit={values => {
        requestChangePassword(values, setLoading);
		}}
		{...props}
	>
		{({ classes, handleSubmit, submitting }) => (
			<form onSubmit={handleSubmit}>
				<div className={classes.form}>
					<div className={classes.input}>
						<Field
							id="verificationCode"
							name="verificationCode"
							label={translate(
                  'authentication.changePassword.verificationCode',
                  { _: 'Verification Code' }
							)}
							render={renderInput}
							disabled={isLoading || submitting}
              />
					</div>
					<p className={classes.input}>
						{translate('authentication.changePassword.cardContent', {
                _:
                  'We just sent you an email with a verification code attached.\nPlease enter this here and type your new password below to reset.',
              })}
					</p>
					<div className={classes.input}>
						<PasswordField
							isFormLoading={isLoading || submitting}
							fieldComponent={renderInput}
							label={translate('authentication.changePassword.newPassword', {
                  _: 'New Password',
                })}
              />
					</div>
				</div>
				<div className={classes.cardActions}>
					<CardActions>
						<Button
							type="submit"
							color="primary"
							disabled={isLoading}
							className={classes.button}
						>
							{isLoading && <CircularProgress size={25} thickness={2}/>}
							{translate('authentication.changePassword.submit', {
                  _: 'Reset Password',
                })}
						</Button>
					</CardActions>
				</div>
			</form>
      )}
	</Form>
  );
});

RequestResetPassword.propTypes = {
  classes: PropTypes.object,
  requestReset: PropTypes.func.isRequired,
};

RequestResetPassword.defaultProps = {
  classes: null,
};

RequestChangePassword.propTypes = {
  classes: PropTypes.object,
  requestChangePassword: PropTypes.func.isRequired,
  overrideFormValidation: PropTypes.func,
};

RequestChangePassword.defaultProps = {
  classes: null,
  overrideFormValidation: null,
};
